/**
 * Persistent key/value storage that works on web and Capacitor native.
 *
 * Web: backed by `localStorage` (synchronous semantics surfaced behind an
 * async wrapper so the API is identical to native).
 *
 * Native: will be backed by `@capacitor/preferences` + platform keychain.
 * Phase 2 of the mobile rollout will swap the native branch to the real
 * plugin; until then the web implementation is a safe no-op fallback.
 */

import { isNative } from './index'

export interface PlatformStorage {
  get(key: string): Promise<string | null>
  set(key: string, value: string): Promise<void>
  remove(key: string): Promise<void>
}

const webStorage: PlatformStorage = {
  async get(key) {
    if (typeof window === 'undefined') return null
    try {
      return window.localStorage.getItem(key)
    } catch {
      return null
    }
  },
  async set(key, value) {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(key, value)
    } catch {
      /* quota exceeded or disabled — ignore */
    }
  },
  async remove(key) {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(key)
    } catch {
      /* ignore */
    }
  },
}

let cached: PlatformStorage | null = null

export function storage(): PlatformStorage {
  if (cached) return cached
  if (!isNative()) {
    cached = webStorage
    return cached
  }
  // Native branch: Capacitor `@capacitor/preferences` lookup is wired in Phase 2.
  // Until then we fall back to the web implementation so the native shell still
  // boots to the login screen with predictable behaviour.
  cached = webStorage
  return cached
}
